import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { setToken } from "../lib/authToken";

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

function TurnstileWidget({ siteKey, onVerify }: { siteKey: string; onVerify: (token: string) => void }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const renderWidget = () => {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      // Clean up any existing widget
      if (widgetIdRef.current !== null) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* ignore */ }
      }
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token: string) => onVerify(token),
      });
    };

    // If turnstile script is already loaded, render immediately
    if (window.turnstile) {
      renderWidget();
      return () => { cancelled = true; };
    }

    // Check if script tag already exists
    const existing = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
    if (!existing) {
      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.onload = () => renderWidget();
      document.head.appendChild(script);
    } else {
      // Script exists but may still be loading
      const check = setInterval(() => {
        if (window.turnstile) {
          clearInterval(check);
          renderWidget();
        }
      }, 100);
      return () => { cancelled = true; clearInterval(check); };
    }

    return () => { cancelled = true; };
  }, [siteKey, onVerify]);

  return <div ref={containerRef} />;
}

export default function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "", name: "", tenant_name: "" });
  const [error, setError] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [turnstileSiteKey, setTurnstileSiteKey] = useState("");

  useEffect(() => {
    api.get<{ turnstile_site_key: string }>("/auth/config")
      .then((res) => setTurnstileSiteKey(res.turnstile_site_key || ""))
      .catch(() => { /* If config fetch fails, proceed without captcha */ });
  }, []);

  const onCaptchaVerify = useCallback((token: string) => {
    setCaptchaToken(token);
  }, []);

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const captchaRequired = turnstileSiteKey !== "";
  const canSubmit = !captchaRequired || captchaToken !== "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      const body: Record<string, string> = { ...form };
      if (captchaToken) {
        body.captcha_token = captchaToken;
      }
      const res = await api.post<{ token: string }>("/auth/signup", body);
      setToken(res.token);
      const planParam = searchParams.get("plan");
      if (planParam) {
        navigate(`/account/billing?plan=${encodeURIComponent(planParam)}`);
      } else {
        navigate("/choose-plan");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Signup failed");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-sm bg-card rounded-lg shadow p-8">
        <h1 className="text-2xl font-bold mb-6">Create your account</h1>
        {error && <p className="text-red-600 text-sm mb-4">{error}</p>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <input type="text" placeholder="Your name" value={form.name} onChange={set("name")} required className="w-full border rounded px-3 py-2 text-sm bg-background" />
          <input type="email" placeholder="Email" value={form.email} onChange={set("email")} required className="w-full border rounded px-3 py-2 text-sm bg-background" />
          <input type="password" placeholder="Password" value={form.password} onChange={set("password")} required className="w-full border rounded px-3 py-2 text-sm bg-background" />
          <input type="text" placeholder="Organization name (optional)" value={form.tenant_name} onChange={set("tenant_name")} className="w-full border rounded px-3 py-2 text-sm bg-background" />
          {turnstileSiteKey && (
            <TurnstileWidget siteKey={turnstileSiteKey} onVerify={onCaptchaVerify} />
          )}
          <button type="submit" disabled={!canSubmit} className="w-full bg-primary text-primary-foreground rounded py-2 text-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed">
            Sign up
          </button>
        </form>
        <p className="text-sm text-muted-foreground mt-4 text-center">
          Already have an account? <Link to="/login" className="text-primary hover:underline">Log in</Link>
        </p>
      </div>
    </div>
  );
}
